import type { EngineState } from '@/lib/types'
import { formatUsd, pnlSign } from '@/lib/format'
import { Metric } from '@/components/common/Metric'
import {
  LinkIcon,
  ChartIcon,
  ListIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  FlameIcon,
} from '@/components/common/icons'

interface TradeStatsBlockProps {
  engine: EngineState
}

/** §142 (B4): блок «Статистика по сделкам» — 6 метрик. Открыто/Текущий PNL — по открытым
 *  связкам; Закрыто/Прибыльных/Убыточных/PNL закрытых — по истории закрытий. Все суммы
 *  движок считает в Decimal (§1.2); фронт только показывает строки. */
export function TradeStatsBlock({ engine }: TradeStatsBlockProps) {
  const s = engine.trade_stats
  const open = s?.open_count ?? engine.active_trades.length
  const currentPnl = s?.current_pnl_usd ?? null
  const closed = s?.closed_count ?? 0
  const profitable = s?.profitable_count ?? 0
  const losing = s?.losing_count ?? 0
  const closedPnl = s?.closed_pnl_usd ?? null

  return (
    <div className="panel">
      <div className="panel__head">
        <div>
          <div className="panel__title">Статистика по сделкам</div>
          <div className="panel__subtitle">
            Открытые связки и итоги закрытых (PNL считает движок — единый PnL-источник эт.12)
          </div>
        </div>
      </div>
      <div className="panel__pad">
        <div className="metrics metrics--6">
          <Metric
            label="Открыто"
            value={open}
            foot="активных связок"
            icon={<LinkIcon size={18} />}
            tone="accent"
          />
          <Metric
            label="Текущий PNL"
            value={formatUsd(currentPnl, { signed: true })}
            foot="по открытым"
            icon={<ChartIcon size={18} />}
            tone={pnlSign(currentPnl) === 'neg' ? 'muted' : 'pos'}
            hint="Суммарный «текущий PnL» открытых связок: комиссии вход+выход, полученный фандинг и разница цен ног (единый PnL-источник эт.12)."
          />
          <Metric
            label="Закрыто"
            value={closed}
            foot="всего в истории"
            icon={<ListIcon size={18} />}
            tone="info"
          />
          <Metric
            label="Прибыльных"
            value={profitable}
            foot="связок с PnL > 0"
            icon={<ArrowUpIcon size={18} />}
            tone="pos"
          />
          <Metric
            label="Убыточных"
            value={losing}
            foot="связок с PnL < 0"
            icon={<ArrowDownIcon size={18} />}
            tone="muted"
          />
          <Metric
            label="PNL закрытых"
            value={formatUsd(closedPnl, { signed: true })}
            foot="сумма по истории"
            icon={<FlameIcon size={18} />}
            tone={pnlSign(closedPnl) === 'neg' ? 'muted' : 'pos'}
            hint="Сумма реализованного PnL всех закрытых связок (из файла истории orders_history)."
          />
        </div>
      </div>
    </div>
  )
}
